import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Clock, ShoppingBag, Store, ArrowRight, Utensils, Star, Heart, RefreshCw, LocateFixed } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import MobileNav from '../components/MobileNav';
import { apiFetch } from '../lib/apiClient';
import useCustomerLocation from '../hooks/useCustomerLocation';
import { sortTrucksByDistance, withTruckDistance } from '../lib/truckLocation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function RatingRow({ truck }) {
  const count = truck.rating_count || 0;
  const avg = truck.rating_avg || 0;
  if (!count) {
    return <span className="text-xs text-muted-foreground">No reviews yet</span>;
  }
  return (
    <div className="flex items-center gap-1 text-sm">
      <Star className="w-4 h-4 fill-primary text-primary" />
      <span className="font-medium">{Number(avg).toFixed(1)}</span>
      <span className="text-muted-foreground">({count})</span>
    </div>
  );
}

const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

function TruckCard({ truck, isFavourite, onToggleFavourite, onReorder, reordering, showReorder }) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.25 }}
    >
      <div className="bg-card rounded-2xl overflow-hidden shadow-md border border-black/5 hover:shadow-lg md:hover:-translate-y-1 transition-shadow duration-200">
        <Link to={`/trucks/${truck.truck_id}`} data-testid={`truck-card-${truck.truck_id}`}>
          <div className="aspect-[4/3] relative">
            <img
              src={truck.photo_url || 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=600'}
              alt={truck.name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-3 left-3">
              <span className="px-3 py-1 rounded-full bg-background/90 text-xs font-medium">
                {truck.cuisine_type || 'Food Truck'}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavourite(truck); }}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background/90 flex items-center justify-center shadow-sm hover:bg-background"
              aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
              data-testid={`favourite-btn-${truck.truck_id}`}
            >
              <Heart className={`w-5 h-5 ${isFavourite ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
            </button>
          </div>
        </Link>
        <div className="p-4">
          <Link to={`/trucks/${truck.truck_id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-lg">{truck.name}</h3>
              <RatingRow truck={truck} />
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {truck.description}
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1 min-w-0">
                <MapPin className="w-4 h-4 shrink-0" />
                <span className="truncate">
                  {truck.location}
                  {truck.exact_location?.note && (
                    <span className="text-foreground/80"> · {truck.exact_location.note}</span>
                  )}
                </span>
              </div>
            </div>
            {truck.distance_label && (
              <p className="text-xs font-medium text-primary mt-2" data-testid={`truck-distance-${truck.truck_id}`}>
                {truck.distance_label}
              </p>
            )}
          </Link>
          {showReorder && (
            <Button
              variant="outline"
              className="w-full mt-3 rounded-xl"
              disabled={reordering}
              onClick={() => onReorder(truck)}
              data-testid={`reorder-btn-${truck.truck_id}`}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reordering ? 'animate-spin' : ''}`} />
              {reordering ? 'Loading…' : 'Reorder last order'}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [favouriteTrucks, setFavouriteTrucks] = useState([]);
  const [reorderingId, setReorderingId] = useState('');
  const { user } = useAuth();
  const { addItem, clearCart } = useCart();
  const navigate = useNavigate();
  const {
    coords: customerCoords,
    status: locationStatus,
    requesting: locationRequesting,
    requestLocation,
  } = useCustomerLocation();

  useEffect(() => {
    const fetchTrucks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/trucks`);
        const data = await response.json();
        setTrucks(data);
      } catch (error) {
        console.error('Failed to fetch trucks:', error);
      } finally {
        setLoading(false);
      }
    };

    // Seed data first, then fetch
    const init = async () => {
      try {
        await fetch(`${API_URL}/api/seed`, { method: 'POST' });
      } catch (e) {
        // Ignore
      }
      fetchTrucks();
    };
    init();
  }, []);

  const fetchFavourites = useCallback(async () => {
    if (!user) {
      setFavouriteIds(new Set());
      setFavouriteTrucks([]);
      return;
    }
    try {
      const response = await apiFetch('/api/favourites');
      if (response.ok) {
        const data = await response.json();
        setFavouriteTrucks(data);
        setFavouriteIds(new Set(data.map((t) => t.truck_id)));
      }
    } catch (error) {
      console.error('Failed to load favourites:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchFavourites();
  }, [fetchFavourites]);

  const handleUseLocation = async () => {
    try {
      await requestLocation();
      toast.success('Showing trucks nearest to you');
    } catch {
      toast.error('Could not use your location. You can still browse all trucks.');
    }
  };

  const toggleFavourite = async (truck) => {
    if (!user) {
      toast.error('Sign in to save favourites');
      return;
    }
    const truckId = truck.truck_id;
    const isFav = favouriteIds.has(truckId);
    // Optimistic update.
    setFavouriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(truckId); else next.add(truckId);
      return next;
    });
    try {
      const response = await apiFetch(`/api/favourites/${truckId}`, {
        method: isFav ? 'DELETE' : 'POST',
      });
      if (!response.ok) throw new Error('Failed');
      await fetchFavourites();
    } catch (error) {
      // Revert on failure.
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(truckId); else next.delete(truckId);
        return next;
      });
      toast.error('Could not update favourites');
    }
  };

  const handleReorder = async (truck) => {
    if (!user) {
      toast.error('Sign in to reorder');
      return;
    }
    setReorderingId(truck.truck_id);
    try {
      const [lastRes, menuRes] = await Promise.all([
        apiFetch(`/api/trucks/${truck.truck_id}/last-order`),
        apiFetch(`/api/trucks/${truck.truck_id}/menu`),
      ]);
      if (!lastRes.ok) throw new Error('No previous order for this truck');
      const lastOrder = await lastRes.json();
      const menu = menuRes.ok ? await menuRes.json() : [];
      const menuById = new Map(menu.filter((m) => m.available !== false).map((m) => [m.item_id, m]));

      const toAdd = [];
      let skipped = 0;
      for (const item of lastOrder.items || []) {
        const menuItem = menuById.get(item.item_id);
        if (!menuItem) { skipped += 1; continue; }
        toAdd.push({ item: menuItem, qty: item.quantity || 1 });
      }
      if (toAdd.length === 0) {
        toast.error('Those items are no longer available');
        return;
      }
      clearCart();
      const truckRef = { truck_id: truck.truck_id, name: truck.name };
      for (const { item, qty } of toAdd) {
        for (let n = 0; n < qty; n += 1) {
          addItem(item, truckRef);
        }
      }
      if (skipped > 0) {
        toast.message(`${skipped} item(s) from your last order are unavailable and were skipped`);
      }
      toast.success('Added your last order to the cart');
      navigate('/checkout');
    } catch (error) {
      toast.error(error.message || 'Could not reorder');
    } finally {
      setReorderingId('');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1726195756069-89e01680de0f?w=800" 
            alt="Food truck"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background" />
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto px-4 pt-24 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="" className="w-5 h-5 rounded-md object-cover" />
              <span className="text-sm font-medium tracking-wide">UofT Campus Food Trucks</span>
            </div>

            <p className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
              Skipd<span className="text-primary">Q</span>
            </p>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Skip the Line,<br />
              <span className="text-primary">Pick Up &amp; Go</span>
            </h1>
            
            <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Pre-order from your favorite campus food trucks. Pick up between classes — hot, fresh, and ready when you arrive.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="rounded-full text-base px-8"
                data-testid="explore-trucks-btn"
                onClick={() => document.getElementById('trucks')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Trucks
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {user?.role === 'vendor' && (
                <Link to="/dashboard">
                  <Button variant="outline" size="lg" className="rounded-full text-base px-8" data-testid="vendor-dashboard-btn">
                    <Store className="w-4 h-4 mr-2" />
                    Vendor Dashboard
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-4 max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-12">How SkipdQ Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Utensils, title: 'Browse Menus', desc: 'Explore food trucks near your next class' },
            { icon: Clock, title: 'Pick a Time', desc: 'Schedule pickup for your break' },
            { icon: ShoppingBag, title: 'Skip the Line', desc: 'Walk up and grab your order' }
          ].map((step, i) => (
            <motion.div
              key={step.title}
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.25, delay: i * 0.05 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <step.icon className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Favourites */}
      {user && favouriteTrucks.length > 0 && (
        <section id="favourites" className="pt-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-8">
              <Heart className="w-6 h-6 fill-primary text-primary" />
              <h2 className="text-2xl font-bold">Your Favourites</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favouriteTrucks.map((truck, i) => (
                <TruckCard
                  key={truck.truck_id}
                  truck={customerCoords ? withTruckDistance(truck, customerCoords) : truck}
                  index={i}
                  isFavourite={favouriteIds.has(truck.truck_id)}
                  onToggleFavourite={toggleFavourite}
                  onReorder={handleReorder}
                  reordering={reorderingId === truck.truck_id}
                  showReorder
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Food Trucks */}
      <section id="trucks" className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold">Campus Food Trucks</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {customerCoords
                  ? 'Sorted by distance from your current location.'
                  : 'Use your location to see the nearest trucks first.'}
              </p>
              {locationStatus === 'denied' && (
                <p className="text-sm text-amber-700 mt-1">Location permission was denied, so trucks are shown in the usual order.</p>
              )}
              {locationStatus === 'unavailable' && (
                <p className="text-sm text-amber-700 mt-1">Location is unavailable on this device, so trucks are shown in the usual order.</p>
              )}
            </div>
            <Button
              type="button"
              variant={customerCoords ? 'outline' : 'default'}
              onClick={handleUseLocation}
              disabled={locationRequesting}
              data-testid="use-my-location-btn"
            >
              <LocateFixed className="w-4 h-4 mr-2" />
              {locationRequesting ? 'Finding you…' : customerCoords ? 'Refresh location' : 'Use my location'}
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-card rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-4 space-y-3">
                    <div className="h-5 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : trucks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No food trucks available right now
            </div>
          ) : (() => {
            const renderGrid = (list, keyPrefix) => (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {list.map((truck, i) => (
                  <TruckCard
                    key={`${keyPrefix}-${truck.truck_id}`}
                    truck={truck}
                    index={i}
                    isFavourite={favouriteIds.has(truck.truck_id)}
                    onToggleFavourite={toggleFavourite}
                    onReorder={handleReorder}
                    reordering={reorderingId === truck.truck_id}
                    showReorder={false}
                  />
                ))}
              </div>
            );

            const sponsored = trucks.filter((t) => t.is_sponsored);
            const rest = trucks.filter((t) => !t.is_sponsored);

            // Always show Sponsored when present — even with location on
            // (previously location mode only rendered "Nearest to you").
            if (customerCoords) {
              const nearestRest = sortTrucksByDistance(rest, customerCoords);
              const nearestSponsored = sortTrucksByDistance(sponsored, customerCoords);
              return (
                <div className="space-y-10">
                  {nearestSponsored.length > 0 && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                        Sponsored
                      </p>
                      {renderGrid(nearestSponsored, 'sponsored')}
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                      Nearest to you
                    </p>
                    {renderGrid(nearestRest, 'nearest')}
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-10">
                {sponsored.length > 0 && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                      Sponsored
                    </p>
                    {renderGrid(sponsored, 'sponsored')}
                  </div>
                )}
                {rest.length > 0 && (
                  <div>
                    {sponsored.length > 0 && (
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                        All trucks
                      </p>
                    )}
                    {renderGrid(rest, 'all')}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Campus Map Placeholder */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center">Find Us on Campus</h2>
          <div className="aspect-video rounded-2xl overflow-hidden bg-card border relative">
            <img 
              src="https://images.pexels.com/photos/7973042/pexels-photo-7973042.jpeg?auto=compress&w=800"
              alt="UofT Campus"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center bg-background/95 px-8 py-6 rounded-2xl">
                <MapPin className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="font-medium">UofT St. George Campus</p>
                <p className="text-sm text-muted-foreground">Multiple locations daily</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-card rounded-2xl border p-8 text-center">
            <Store className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Own a Food Truck?</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Join SkipdQ and reach thousands of hungry UofT students. 
              Pre-orders, guaranteed payments, zero platform fees.
            </p>
            <Link to="/vendor">
              <Button size="lg" variant="outline" className="rounded-full" data-testid="vendor-cta-btn">
                <Store className="w-4 h-4 mr-2" />
                Become a Vendor
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-8 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-5 text-sm text-muted-foreground flex-wrap">
          <Link to="/help" className="hover:text-foreground">Help</Link>
          <span>•</span>
          <Link to="/privacy-policy" className="hover:text-foreground">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
        </div>
      </section>

      <MobileNav />
    </div>
  );
}
